import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';

const ChallengeLeaderboard = ({ challengeId, tenantId, currentUserId }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, [challengeId]);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('user_challenge_progress')
        ?.select(`
          *,
          user:user_profiles(id, full_name, avatar_url)
        `)
        ?.eq('challenge_id', challengeId)
        ?.eq('tenant_id', tenantId)
        ?.order('current_value', { ascending: false });

      if (error) throw error;

      const formattedData = data?.map((entry, index) => ({
        rank: index + 1,
        userId: entry?.user?.id,
        name: entry?.user?.full_name,
        avatar: entry?.user?.avatar_url,
        progress: entry?.current_value,
        isCompleted: entry?.is_completed,
        isCurrentUser: entry?.user_id === currentUserId
      }));

      setLeaderboard(formattedData || []);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankColor = (rank) => {
    switch (rank) {
      case 1: return 'text-yellow-500';
      case 2: return 'text-gray-400';
      case 3: return 'text-orange-600';
      default: return 'text-muted-foreground';
    }
  };

  const getRankIcon = (rank) => {
    if (rank <= 3) return 'Medal';
    return 'User';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  if (leaderboard?.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="Trophy" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
        <p className="text-muted-foreground">No participants yet</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
        <Icon name="Trophy" size={20} color="var(--color-primary)" />
        Leaderboard
      </h3>
      <div className="space-y-2">
        {leaderboard?.map((entry) => (
          <div
            key={entry?.userId}
            className={`p-4 rounded-lg flex items-center gap-4 ${
              entry?.isCurrentUser
                ? 'bg-primary/10 border-2 border-primary' :'bg-muted/50'
            }`}
          >
            {/* Rank */}
            <div className={`w-8 text-center font-bold text-lg ${
              getRankColor(entry?.rank)
            }`}>
              {entry?.rank <= 3 ? (
                <Icon name={getRankIcon(entry?.rank)} size={24} />
              ) : (
                entry?.rank
              )}
            </div>

            {/* Avatar */}
            {entry?.avatar ? (
              <AppImage
                src={entry?.avatar}
                alt={entry?.name || 'Participant'}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <Icon name="User" size={24} color="var(--color-primary)" />
              </div>
            )}

            {/* Name */}
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-foreground truncate">
                {entry?.name || 'Unknown'}
                {entry?.isCurrentUser && (
                  <span className="ml-2 text-xs text-primary">(You)</span>
                )}
              </h4>
              {entry?.isCompleted && (
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <Icon name="CheckCircle" size={14} />
                  Completed
                </div>
              )}
            </div>

            {/* Progress */}
            <div className="text-right">
              <div className="font-bold text-lg text-foreground">
                {entry?.progress}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChallengeLeaderboard;